/**
 * Nexora PDV - Offline Service
 *
 * Monitors browser online/offline status, provides reactive connectivity
 * state, and automates syncing of pending sales when connectivity returns.
 *
 * @module offline.service
 */

import {
  getPendingSales,
  removePendingSale,
  markPendingSaleError,
  saveProduct,
  clearProducts,
  clearAllOfflineData,
  setLastSync,
  getLastSync,
  getPendingSalesCount,
} from './offline-db';
import type { PosUiPaymentMethod } from '../../features/pos/posSaleFlow';

/**
 * Valida o payload enfileirado e converte para o fluxo online
 * (terminalId + items + payments). Falha com erro descritivo quando
 * o registro não tem os campos mínimos — o chamador mantém na fila.
 */
/** Os meios que a tela do PDV emite — e os unicos que a fila sabe reexecutar. */
const METODOS_DA_TELA: ReadonlySet<string> = new Set<PosUiPaymentMethod>([
  'CASH',
  'CREDIT_CARD',
  'DEBIT_CARD',
  'PIX',
]);

function toOnlineFlowCommand(
  tenantId: string,
  payload: Record<string, unknown>,
): {
  terminalId: string;
  items: Array<{
    productId: string;
    name: string;
    sku: string;
    quantity: number;
    unitValue: number;
    unitCost: number;
  }>;
  payments: Array<{ method: PosUiPaymentMethod; value: number }>;
} {
  void tenantId;
  const terminalId = payload['terminalId'];
  const items = payload['items'];
  const payments = payload['payments'];
  if (typeof terminalId !== 'string' || terminalId.length === 0) {
    throw new Error('Pending sale sem terminalId — descarte manual necessário');
  }
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Pending sale sem itens — descarte manual necessário');
  }
  if (!Array.isArray(payments) || payments.length === 0) {
    throw new Error('Pending sale sem pagamentos — descarte manual necessário');
  }
  return {
    terminalId,
    items: items.map((item) => {
      const row = item as Record<string, unknown>;
      if (typeof row['productId'] !== 'string' || typeof row['quantity'] !== 'number' || typeof row['unitValue'] !== 'number') {
        throw new Error('Item da venda pendente inválido — descarte manual necessário');
      }
      return {
        productId: row['productId'] as string,
        name: (row['name'] as string) ?? (row['productId'] as string),
        sku: (row['sku'] as string) ?? (row['productId'] as string),
        quantity: row['quantity'] as number,
        unitValue: row['unitValue'] as number,
        unitCost: typeof row['unitCost'] === 'number' ? (row['unitCost'] as number) : 0,
      };
    }),
    payments: payments.map((payment) => {
      const row = payment as Record<string, unknown>;
      if (typeof row['method'] !== 'string' || typeof row['value'] !== 'number') {
        throw new Error('Pagamento da venda pendente inválido — descarte manual necessário');
      }
      const metodo = row['method'] as string;
      if (!METODOS_DA_TELA.has(metodo)) {
        /* Reexecutar com um meio que a tela nao conhece cairia num mapeamento
           errado — o defeito de `'CREDIT' -> 'OTHER'`. Recusar e mais seguro
           que gravar o pagamento como outra coisa. */
        throw new Error(
          `Pagamento da venda pendente com meio desconhecido (${metodo}) — descarte manual necessário`,
        );
      }
      return { method: metodo as PosUiPaymentMethod, value: row['value'] as number };
    }),
  };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OfflineEventType =
  | 'offline-status-changed'
  | 'sync-started'
  | 'sync-completed'
  | 'sync-error';

export interface OfflineEvent {
  type: OfflineEventType;
  payload: unknown;
  timestamp: number;
}

export type OfflineEventListener = (event: OfflineEvent) => void;

type Unsubscriber = () => void;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let _isOnline = navigator.onLine;
const listeners = new Set<OfflineEventListener>();

// ---------------------------------------------------------------------------
// Event emitter
// ---------------------------------------------------------------------------

function emit(type: OfflineEventType, payload: unknown): void {
  const event: OfflineEvent = { type, payload, timestamp: Date.now() };
  listeners.forEach((fn) => {
    try {
      fn(event);
    } catch {
      // Swallow listener errors so one bad listener doesn't break the chain
    }
  });
}

// ---------------------------------------------------------------------------
// Connectivity monitoring
// ---------------------------------------------------------------------------

function handleOnline(): void {
  _isOnline = true;
  emit('offline-status-changed', { isOnline: true });

  // Attempt to sync pending sales when coming back online
  syncPendingSales().catch(() => {
    /* errors are emitted inside syncPendingSales */
  });
}

function handleOffline(): void {
  _isOnline = false;
  emit('offline-status-changed', { isOnline: false });
}

let _initialized = false;

function ensureListening(): void {
  if (_initialized) return;
  _initialized = true;

  if (typeof window !== 'undefined') {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns the current online status synchronously.
 */
export function isOnline(): boolean {
  return _isOnline;
}

/**
 * Subscribe to offline service events.
 * Returns an unsubscribe function.
 */
export function onOfflineEvent(listener: OfflineEventListener): Unsubscriber {
  ensureListening();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * One-time event promise. Resolves when the given event type fires.
 */
export function waitForEvent(type: OfflineEventType): Promise<OfflineEvent> {
  return new Promise((resolve) => {
    const unsub = onOfflineEvent((event) => {
      if (event.type === type) {
        unsub();
        resolve(event);
      }
    });
  });
}

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

/**
 * Attempt to synchronise all pending offline sales with the server.
 *
 * For each pending sale:
 *   1. POST the payload to the API.
 *   2. On success: remove from the pending queue.
 *   3. On failure: increment retry count, store the error.
 *
 * If any sale fails with a non-retryable status (4xx), it is kept in the
 * queue with the error recorded — the caller / UI can decide to discard it.
 */
export async function syncPendingSales(): Promise<{
  synced: number;
  failed: number;
  errors: string[];
}> {
  if (!_isOnline) {
    return { synced: 0, failed: 0, errors: ['Device is offline'] };
  }

  const pending = await getPendingSales();

  if (pending.length === 0) {
    return { synced: 0, failed: 0, errors: [] };
  }

  emit('sync-started', { count: pending.length });

  let synced = 0;
  let failed = 0;
  const errors: string[] = [];

  for (const sale of pending) {
    try {
      // Sem segredo do terminal no browser, o endpoint assinado
      // POST /pos/sales/sync é inviável: reexecuta o fluxo online
      // (start → items → payments → complete) com o offlineOperationId
      // original para rastreabilidade. Remove da fila só após o complete.
      const { submitPosSaleOnline } = await import('../../features/pos/posSaleFlow');
      const command = toOnlineFlowCommand(sale.tenantId, sale.payload);
      await submitPosSaleOnline({
        ...command,
        offlineOperationId: sale.offlineOperationId,
      });
      await removePendingSale(sale.id!);
      synced++;
    } catch (error) {
      failed++;
      const message = (error as Error).message ?? 'Unknown sync error';

      await markPendingSaleError(sale.id!, message);
      errors.push(message);
    }
  }

  if (failed === 0) {
    await setLastSync(new Date());
    emit('sync-completed', { synced, failed });
    // NFR-POS-001: clear offline data after confirmed sync
    await clearAllOfflineData();
  } else {
    emit('sync-error', { synced, failed, errors });
  }

  return { synced, failed, errors };
}

// ---------------------------------------------------------------------------
// Product catalog sync helper
// ---------------------------------------------------------------------------

/**
 * Fetch the product catalog from the API and cache it in IndexedDB.
 * Replaces the entire local product cache.
 */
export async function syncProductCatalog(
  tenantId: string,
): Promise<{ cached: number }> {
  // Contrato real via helper compartilhado (GET /products + /inventory/balance)
  const { fetchPosCatalog } = await import('../../features/pos/posCatalog');
  const products = await fetchPosCatalog();

  await clearProducts();

  for (const product of products) {
    await saveProduct({
      id: product.id,
      code: product.code,
      name: product.name,
      salePrice: product.salePrice,
      unit: product.unit,
      stock: product.stock,
      tenantId,
    });
  }

  await setLastSync(new Date());
  return { cached: products.length };
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

/**
 * Initialize the offline service.
 * Call once when the application boots.
 */
export function initOfflineService(): void {
  ensureListening();
}

// ---------------------------------------------------------------------------
// Re-export for convenience
// ---------------------------------------------------------------------------

export { getPendingSalesCount, getLastSync };
